import { Icon, Tooltip } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { ampli } from 'lib-shared-tracking';
import { SearchUserHit, User } from 'lib-shared-types-api';
import { useMemo } from 'react';

import { AwayStatus } from 'apis/presence';
import { CustomAwayStatus } from 'components/presence/components/customAwayStatus';
import { getPresenceIndicator, getStatus } from 'components/presence/utils';
import { getUserDisplayName } from 'utils/user';

import { PresenceOverlayPopoverNew } from './PresenceOverlayPopoverNew';
import styles from './presenceListItem.module.scss';

type PresenceListItemProps = {
  member: SearchUserHit;
  ownProfile: User;
  awayStatuses: AwayStatus[];
  isFavorite: boolean;
  onAddToFavorites: (userId: string) => void;
  onRemoveFromFavorites: (userId: string) => void;
};

export const PresenceListItem = ({
  member,
  ownProfile,
  awayStatuses,
  isFavorite,
  onAddToFavorites,
  onRemoveFromFavorites,
}: PresenceListItemProps): JSX.Element => {
  const isOwnAgent = ownProfile.id === member.id;
  const status = getStatus(member);
  const { icon, tooltip } = getPresenceIndicator(member);

  const isWorking = status === 'working';
  const isAway = status === 'away';
  const isOffline = status === 'offline';

  const rootClass = classnames({
    [styles.root]: true,
    [styles.rootOnline]: isWorking,
    [styles.rootAway]: isAway,
    [styles.rootOffline]: isOffline,
  });

  const customAwayStatus = useMemo(() => {
    if (!isAway || !awayStatuses.length) {
      return null;
    }

    const awayStatus = awayStatuses.find(
      ({ id }) => id === member.custom_away_status?.custom_away_status_id,
    );

    if (!awayStatus) {
      return null;
    }

    return (
      <CustomAwayStatus
        awayStatus={awayStatus}
        statusUpdatedAt={member.custom_away_status?.status_updated_at || null}
      />
    );
  }, [isAway, member.custom_away_status, awayStatuses]);

  const memberName = getUserDisplayName(member);

  const handleClick = () => {
    ampli.presenceListItemClicked();
  };

  return (
    <PresenceOverlayPopoverNew
      member={member}
      ownProfile={ownProfile}
      isFavorite={isFavorite}
      onAddToFavorites={onAddToFavorites}
      onRemoveFromFavorites={onRemoveFromFavorites}
    >
      <li className={rootClass} onClick={handleClick}>
        <div className={styles.container}>
          <Tooltip className={styles.iconTooltip} content={tooltip}>
            {/* @ts-expect-error Custom size not supported by types, but in practice it works */}
            <Icon name={icon} className={styles.icon} size={12} />
          </Tooltip>
          <span className={styles.name}>
            {memberName} {isOwnAgent ? ' (you)' : ''}
          </span>
          {customAwayStatus}
        </div>
      </li>
    </PresenceOverlayPopoverNew>
  );
};
